import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { type Roadmap } from "@/types";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Map } from "lucide-react";

type RoadmapSelectorProps = {
  companyId: string;
  selectedRoadmapId: string | null;
  onRoadmapSelect: (roadmapId: string) => void;
};

const RoadmapSelector = ({
  companyId,
  selectedRoadmapId,
  onRoadmapSelect,
}: RoadmapSelectorProps) => {
  const [roadmaps, setRoadmaps] = useState<Roadmap[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchRoadmaps = async () => {
      if (!companyId) {
        console.log("RoadmapSelector: No companyId provided, skipping fetch");
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        console.log("RoadmapSelector: Fetching roadmaps for company:", companyId);

        // Fetch roadmaps
        const { data: roadmapsData, error: roadmapsError } = await supabase
          .from("roadmaps")
          .select("*")
          .eq("company_id", companyId)
          .order("created_at", { ascending: true });

        if (roadmapsError) {
          console.error("Error fetching roadmaps:", roadmapsError);
          setIsLoading(false);
          return;
        }

        console.log("RoadmapSelector: Found roadmaps:", roadmapsData?.length || 0);

        if (roadmapsData && roadmapsData.length > 0) {
          // Fetch statuses for each roadmap
          const roadmapsWithStatuses = await Promise.all(
            roadmapsData.map(async (roadmap) => {
              const { data: statusesData, error: statusesError } = await supabase
                .from("roadmap_statuses")
                .select("*")
                .eq("roadmap_id", roadmap.id)
                .order("position", { ascending: true });

              if (statusesError) {
                console.error(
                  `Error fetching statuses for roadmap ${roadmap.id}:`,
                  statusesError
                );
                return {
                  ...roadmap,
                  statuses: [],
                };
              }

              return {
                ...roadmap,
                statuses: statusesData || [],
              };
            })
          );

          setRoadmaps(roadmapsWithStatuses);
          
          // If no roadmap is selected, select the default one or the first one
          if (!selectedRoadmapId) {
            const defaultRoadmap = roadmapsWithStatuses.find((r) => r.is_default);
            const roadmapToSelect = defaultRoadmap?.id || roadmapsWithStatuses[0]?.id;
            if (roadmapToSelect) {
              onRoadmapSelect(roadmapToSelect);
            }
          }
        } else {
          setRoadmaps([]);
        }
      } catch (error) {
        console.error("Error in fetchRoadmaps:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchRoadmaps();
  }, [companyId]); // Remove selectedRoadmapId and onRoadmapSelect from deps to avoid infinite loops

  if (isLoading) {
    return <div className="text-sm text-muted-foreground">Loading roadmaps...</div>;
  }

  if (roadmaps.length === 0) {
    return <div className="text-sm text-muted-foreground">No roadmaps available</div>;
  }

  return (
    <div className="flex items-center gap-2 mb-4">
      <Map className="h-5 w-5 text-muted-foreground" />
      <Select
        value={selectedRoadmapId || ""}
        onValueChange={(value) => onRoadmapSelect(value)}
      >
        <SelectTrigger className="w-[250px]">
          <SelectValue placeholder="Select a roadmap" />
        </SelectTrigger>
        <SelectContent>
          {roadmaps.map((roadmap) => (
            <SelectItem key={roadmap.id} value={roadmap.id}>
              <div className="flex items-center gap-2">
                {roadmap.name}
                {roadmap.is_default && (
                  <span className="text-xs text-muted-foreground">(Default)</span>
                )}
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

export default RoadmapSelector;
